import DeleteIcon from '@mui/icons-material/Delete'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material'
import { memo } from 'react'

import type { Scout } from '../types'

interface DeleteScoutDialogProps {
  open: boolean
  scout: Scout | null
  isDeleting?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const DeleteScoutDialog = memo<DeleteScoutDialogProps>(
  ({ open, scout, isDeleting = false, onConfirm, onCancel }) => {
    const theme = useTheme()

    return (
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DeleteIcon sx={{ color: theme.palette.error.main }} />
          <Typography variant="h6" component="span">
            Delete Scout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            Are you sure you want to delete{' '}
            <strong>
              {scout?.firstName} {scout?.lastName}
            </strong>
            ?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: theme.palette.text.secondary }}>
            This action cannot be undone. All data associated with this scout will be permanently
            removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting && <CircularProgress size={16} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: `0 4px 8px ${alpha(theme.palette.error.main, 0.3)}`,
              },
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Scout'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)

DeleteScoutDialog.displayName = 'DeleteScoutDialog'
